import React from 'react';
import { Badge } from '@fluentui/react-components';
import { ArrowSyncRegular, ClockRegular, CheckmarkCircleRegular, ErrorCircleRegular } from '@fluentui/react-icons';
import { useStatusBadgeStyles } from './StatusBadge.styles';
import { RollupStatus } from '../../types';

interface StatusBadgeProps {
    status?: RollupStatus;
    label: string;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, label }) => {
    const styles = useStatusBadgeStyles();

    const getBadgeIcon = () => {
        switch (status) {
            case RollupStatus.InProgress:
                return <ArrowSyncRegular fontSize={12} />;
            case RollupStatus.NotStarted:
                return <ClockRegular fontSize={12} />;
            case RollupStatus.Completed:
                return <CheckmarkCircleRegular fontSize={12} />;
            case RollupStatus.Expired:
                return <ErrorCircleRegular fontSize={12} />;
            default:
                return <ClockRegular fontSize={12} />;
        }
    };

    const getBadgeProps = () => {
        switch (status) {
            case RollupStatus.InProgress:
                return {
                    appearance: 'filled' as const,
                    color: 'informative' as const,
                    className: styles.pendingBadge,
                };
            case RollupStatus.Completed:
                return {
                    appearance: 'tint' as const,
                    color: 'subtle' as const,
                    className: styles.successBadge,
                };
            case RollupStatus.Expired:
                return {
                    appearance: 'tint' as const,
                    color: 'subtle' as const,
                    className: styles.blockedBadge,
                };
            case RollupStatus.NotStarted:
            default:
                return {
                    appearance: 'tint' as const,
                    color: 'subtle' as const,
                    className: styles.neutralBadge,
                };
        }
    };

    const badgeProps = getBadgeProps();

    return (
        <Badge
            appearance={badgeProps.appearance}
            color={badgeProps.color}
            size="medium"
            className={badgeProps.className}
            style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
        >
            {getBadgeIcon()}
            {label}
        </Badge>
    );
};
